import type { QuestionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';

import { cn } from '@repo/lib/cn';
import { questionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';
import { CircleAlertIcon, TypeIcon } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import type { Segment } from '@/hooks/questions/useQuestionFillForm';

import { QuestionContentFormField, QuestionFillFormGap } from '@/components/features/quiz/question/question-builder';
import { Button } from '@/components/ui/button';
import { ErrorMessage, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tag } from '@/components/ui/tags';
import { useQuestionFillForm } from '@/hooks/questions';
import { questionsLabel } from '@/lib/questions-labels';
import { CursorUtils } from '@/utils/cursor';
import { formErrorToast } from '@/utils/form';

import type { QuestionFillFormProps } from '.';

function scrollToGapForm(gapKey: string) {
  const gapElement = document.querySelector(`[data-gap-form-id="${gapKey}"]`) as HTMLDivElement | null;

  if (!gapElement) return;

  gapElement.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });

  gapElement.classList.add('ring-3', 'ring-fill-brand/60');
  setTimeout(() => {
    gapElement.classList.remove('ring-3', 'ring-fill-brand/60');
  }, 2000);
}

function findLastTextSegmentIndex(segments: Segment[]) {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]?.type === 'text') {
      return i;
    }
  }
  return -1;
}

function findFirstTextSegmentIndex(segments: Segment[]) {
  for (let i = 0; i < segments.length; i++) {
    if (segments[i]?.type === 'text') {
      return i;
    }
  }
  return -1;
}

interface TextSegmentProps {
  segment: Segment;
  index: number;
  isOnlySegment: boolean;
  onFocus: (index: number) => void;
  onInput: (index: number, e: React.FormEvent<HTMLSpanElement>) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent<HTMLSpanElement>) => void;
}

function TextSegment(textSegmentProps: TextSegmentProps) {
  const { segment, index, isOnlySegment, onFocus, onInput, onKeyDown } = textSegmentProps;

  const handleFocus = useCallback(() => {
    onFocus(index);
  }, [onFocus, index]);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLSpanElement>) => {
      onInput(index, e);
    },
    [onInput, index]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      onKeyDown(index, e);
    },
    [onKeyDown, index]
  );

  return (
    <span
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      data-text-segment
      data-segment-index={index}
      onFocus={handleFocus}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      className={cn(
        'outline-none whitespace-pre-wrap px-1',
        isOnlySegment && !segment.content && 'empty:before:content-[\'Type_your_question_here._Use_"__"_to_add_a_blank...\'] empty:before:text-muted-foreground'
      )}
    >
      {segment.content}
    </span>
  );
}

interface GapSegmentProps {
  gapKey: string;
  displayContent: string;
  gapNumber: number | null;
  index: number;
  onBlankClick: (gapKey: string, e: React.MouseEvent<HTMLDivElement>) => void;
  onRemoveBlank: (gapKey: string, e: React.MouseEvent<HTMLButtonElement>) => void;
}

function GapSegment(gapSegmentProps: GapSegmentProps) {
  const { gapKey, displayContent, gapNumber, index, onBlankClick, onRemoveBlank } = gapSegmentProps;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onBlankClick(gapKey, e);
    },
    [onBlankClick, gapKey]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onRemoveBlank(gapKey, e);
    },
    [onRemoveBlank, gapKey]
  );

  return (
    <Tag data-segment-index={index} data-gap-segment variant="muted" border={true} contentEditable={false} onClick={handleClick} onRemove={handleRemove}>
      {gapNumber != null && <span className="bg-gray text-gray-foreground flex size-4.5 items-center justify-center rounded-full text-xs opacity-75">{gapNumber}</span>}
      {displayContent}
    </Tag>
  );
}

interface GapMeta {
  content: string;
  gapNumber: number;
}

function getCorrectOptionsContent(options: QuestionFillGapsSchema['gaps'][number]['options'] | undefined): string {
  if (!options) {
    return '';
  }

  const correctContents: string[] = [];
  for (const option of options) {
    if (!option?.correct) {
      continue;
    }

    const trimmed = option.content?.trim();
    if (trimmed) {
      correctContents.push(trimmed);
    }
  }

  return correctContents.join('/');
}

export function QuestionFillForm(questionFillFormProps: QuestionFillFormProps) {
  const { data, onSubmit = () => {}, className, ...props } = questionFillFormProps;

  const { form, fillQuestionGaps, segments, segmentsRef, updateSegmentContent, insertGapAtSegment, removeGap } = useQuestionFillForm(data);
  const gaps = useWatch({
    control: form.control,
    name: 'gaps',
  });

  const lastFocusedSegmentIndexRef = useRef<number>(0);

  const gapMetaById = useMemo<Map<string, GapMeta>>(() => {
    const map = new Map<string, GapMeta>();
    if (!gaps) {
      return map;
    }

    for (let index = 0; index < gaps.length; index += 1) {
      const gap = gaps[index];
      if (!gap?.id) {
        continue;
      }

      map.set(gap.id, {
        content: getCorrectOptionsContent(gap.options),
        gapNumber: index + 1,
      });
    }

    return map;
  }, [gaps]);

  const handleSegmentFocus = useCallback((index: number) => {
    lastFocusedSegmentIndexRef.current = index;
  }, []);

  const handleAddGapClick = useCallback(() => {
    const currentSegments = segmentsRef.current;
    let segmentIndex = lastFocusedSegmentIndexRef.current;
    const segment = currentSegments[segmentIndex];

    if (!segment || segment.type !== 'text') {
      const firstText = findFirstTextSegmentIndex(currentSegments);
      segmentIndex = firstText >= 0 ? firstText : 0;
    }

    const targetSegment = currentSegments[segmentIndex];
    if (targetSegment?.type !== 'text') return;

    const cursorPos = CursorUtils.getCursorPosition(segmentIndex);
    const position = cursorPos?.position ?? 0;
    const beforeText = targetSegment.content.slice(0, position);
    const afterText = targetSegment.content.slice(position);

    const { newTextSegmentIndex } = insertGapAtSegment(segmentIndex, beforeText, afterText);

    requestAnimationFrame(() => {
      CursorUtils.focusAtStart(newTextSegmentIndex);
    });
  }, [insertGapAtSegment, segmentsRef]);

  const handleTextInput = useCallback(
    (segmentIndex: number, e: React.FormEvent<HTMLSpanElement>) => {
      const target = e.currentTarget;
      const newContent = target.textContent || '';
      const underscoreIndex = newContent.indexOf('__');

      if (underscoreIndex !== -1) {
        e.preventDefault();
        e.stopPropagation();

        const beforeText = newContent.substring(0, underscoreIndex);
        const afterText = newContent.substring(underscoreIndex + 2);

        const { newTextSegmentIndex } = insertGapAtSegment(segmentIndex, beforeText, afterText);

        requestAnimationFrame(() => {
          CursorUtils.focusAtStart(newTextSegmentIndex);
        });
      } else {
        updateSegmentContent(segmentIndex, newContent);
      }
    },
    [insertGapAtSegment, updateSegmentContent]
  );

  const handleKeyDown = useCallback(
    (segmentIndex: number, e: React.KeyboardEvent<HTMLSpanElement>) => {
      const cursorPos = CursorUtils.getCursorPosition(segmentIndex);

      if (cursorPos?.hasSelection) {
        return;
      }

      const currentSegments = segmentsRef.current;

      if (e.key === 'Backspace' && cursorPos?.isFirst) {
        if (segmentIndex > 0 && currentSegments[segmentIndex - 1]?.type === 'gap') {
          const blankSegment = currentSegments[segmentIndex - 1];
          if (blankSegment?.gapKey) {
            e.preventDefault();
            e.stopPropagation();

            const { cursorSegmentInfo } = removeGap(blankSegment.gapKey);
            if (cursorSegmentInfo) {
              requestAnimationFrame(() => {
                CursorUtils.focusAtPosition(cursorSegmentInfo.index, cursorSegmentInfo.offset);
              });
            }
          }
        }
        return;
      }

      if (e.key === 'Delete' && cursorPos?.isLast) {
        if (segmentIndex < currentSegments.length - 1 && currentSegments[segmentIndex + 1]?.type === 'gap') {
          const blankSegment = currentSegments[segmentIndex + 1];
          if (blankSegment?.gapKey) {
            e.preventDefault();
            e.stopPropagation();

            const { cursorSegmentInfo } = removeGap(blankSegment.gapKey);
            if (cursorSegmentInfo) {
              requestAnimationFrame(() => {
                CursorUtils.focusAtPosition(cursorSegmentInfo.index, cursorSegmentInfo.offset);
              });
            }
          }
        }
        return;
      }

      if (e.key === 'ArrowLeft' && cursorPos?.isFirst) {
        // Find previous text segment
        for (let i = segmentIndex - 1; i >= 0; i--) {
          if (currentSegments[i]?.type === 'text') {
            e.preventDefault();
            e.stopPropagation();
            requestAnimationFrame(() => {
              CursorUtils.focusAtEnd(i);
            });
            break;
          }
        }
      }

      if (e.key === 'ArrowRight' && cursorPos?.isLast) {
        // Find next text segment
        for (let i = segmentIndex + 1; i < currentSegments.length; i++) {
          if (currentSegments[i]?.type === 'text') {
            e.preventDefault();
            e.stopPropagation();
            requestAnimationFrame(() => {
              CursorUtils.focusAtStart(i);
            });
            break;
          }
        }
      }
    },
    [segmentsRef, removeGap]
  );

  const handleBlankClick = useCallback((gapKey: string, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    scrollToGapForm(gapKey);
  }, []);

  const handleRemoveBlank = useCallback(
    (gapKey: string, e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const { cursorSegmentInfo } = removeGap(gapKey);
      if (cursorSegmentInfo) {
        requestAnimationFrame(() => {
          CursorUtils.focusAtPosition(cursorSegmentInfo.index, cursorSegmentInfo.offset);
        });
      }
    },
    [removeGap]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      const lastTextSegmentIndex = findLastTextSegmentIndex(segments);
      if (lastTextSegmentIndex !== -1) {
        requestAnimationFrame(() => {
          CursorUtils.focusAtEnd(lastTextSegmentIndex);
        });
      }
    },
    [segments]
  );

  const isOnlySegment = segments.length === 1;

  return (
    <Form {...form}>
      <form {...props} noValidate onSubmit={form.handleSubmit(onSubmit, () => formErrorToast(form, questionFillGapsSchema))} className={cn('h-full', className)}>
        <div className="divide-border-primary divide-y">
          <QuestionContentFormField />
          <div className="space-y-6 p-3">
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <CircleAlertIcon className="size-4" />
              <p>{data?.type && questionsLabel[data.type].builderDesc}</p>
            </div>
            <ErrorMessage className="my-4 min-h-0 w-full" name={'gaps.root'} />
            <FormField
              name={'gapsContent'}
              render={() => (
                <FormItem className={'relative w-full'}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="space-y-1">
                      <FormLabel>Treść z lukami</FormLabel>
                      <FormDescription>Wpisz &quot;__&quot; aby automatycznie dodać lukę lub użyj przycisku</FormDescription>
                    </div>
                    <Button type="button" variant="tertiary" size="sm" onClick={handleAddGapClick}>
                      <TypeIcon />
                      Dodaj Lukę
                    </Button>
                  </div>

                  <FormControl>
                    <div className="relative">
                      <div
                        onClick={handleContainerClick}
                        className={
                          'border-border-primary transition-outline placeholder:text-muted-foreground/50 outline-focus-ring group-aria-invalid:border-destructive/50 group-aria-invalid:focus-within:outline-ring-destructive/50 min-h-32 w-full overflow-hidden rounded-md border bg-transparent px-3 py-1 text-base leading-relaxed outline-0 transition-colors duration-150 focus-within:z-10 focus-within:outline-3 disabled:cursor-not-allowed disabled:opacity-60'
                        }
                      >
                        {segments.map((segment, index) => {
                          if (segment.type === 'text') {
                            return (
                              <TextSegment
                                key={segment.id}
                                segment={segment}
                                index={index}
                                isOnlySegment={isOnlySegment}
                                onFocus={handleSegmentFocus}
                                onInput={handleTextInput}
                                onKeyDown={handleKeyDown}
                              />
                            );
                          }

                          const gapKey = segment.gapKey ?? '';
                          const gapMeta = gapMetaById.get(gapKey);

                          return (
                            <GapSegment
                              key={segment.id}
                              gapKey={gapKey}
                              displayContent={gapMeta?.content || gapKey}
                              gapNumber={gapMeta?.gapNumber ?? null}
                              index={index}
                              onBlankClick={handleBlankClick}
                              onRemoveBlank={handleRemoveBlank}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="space-y-6">
              <ErrorMessage className="my-4 min-h-0 w-full" name={'gaps'} />
              {fillQuestionGaps.fields.map((field, index) => (
                <QuestionFillFormGap key={field.id} gapIndex={index} />
              ))}
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
